import { configureScope, init } from '@sentry/browser'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import {
  getStylesComcast,
  getStylesTizen,
  getStylesVIZIO,
} from '../../shared/captionsMapping'
import {
  getSponsorshipInfoComcast,
  getSponsorshipInfoTizen,
  getSponsorshipInfoVIZIO,
  loadScript,
  platformStoreDelete,
  platformStoreGet,
  platformTitle,
  triggerSentryError,
} from '../../shared/helpers'
import {
  onKeyDown,
  registerTizenMediaKeys,
  registerTizenNumericalKeys,
} from '../../shared/keys/keyEvents'
import setupFocus from '../../shared/spatialNavigationFocus'
import { logClientEvent } from '../../shared/clientLogger'
import { checkDeviceId } from './actions/contentActions'
import App from './App'
import AppLaunchErrorScreen from './components/AppLaunchErrorScreen'
import configuration from './configuration'
import './index.scss'
import googleAnalytics4 from './lib/googleAnalytics'
import tts from './lib/TTS'
import './lib/webOSTVjs-1.2.12/webOSTV'
import Platform from './platforms'
import initActivationVerification from './services/CheckUserActivationService'
import { store } from './store'
// import { attachLogger } from '../../shared/screenLogger.js'
// attachLogger()

setupFocus()

// If Sentry isn't defined, mock it so we don't have to check for it everywhere
if (typeof Sentry === 'undefined') {
  window.Sentry = {
    captureException: () => {
      /* no-op */
    },
    configureScope: () => {
      /* no-op */
    },
    init: () => {
      /* no-op */
    },
  }
}

if (BUILD_NUMBER) {
  // Set up Sentry
  const SENTRY_PUBLIC_DSN =
    configuration.sentryDsns[window.location.host] ||
    configuration.sentryDsns.defaults

  init({
    dsn: SENTRY_PUBLIC_DSN,
    maxBreadcrumbs: 50,
    ignoreErrors: [/.*Blocked a frame with origin.*/],
    normalizeDepth: 5,
    release: BUILD_NUMBER,
  })
  configureScope((scope) => {
    scope.setExtra('BUILD_NUMBER', BUILD_NUMBER)
    scope.setTag('BUILD_NUMBER', BUILD_NUMBER)
    scope.setTag('platformTitle', platformTitle())
  })
}

// Remove the autofocused element that we use as a workaround for Tizen TTS reading "Document Web"
try {
  document.getElementById('prevent_document_web').remove()
} catch (e) {
  // empty block
}

// Commenting out as this hasn't been used in a while but we still might want the token
// datadogLogs.init({
//   clientToken: 'pub62d3c26ce353104ea188723be3771788',
//   forwardErrorsToLogs: true,
//   sampleRate: 1, // only sample logs from 1% of users
// })

async function main() {
  platformStoreDelete('connectionMessage')
  window.forceCheckDebounce = null
  let captionsOn = platformStoreGet('captionsOn') === 'true'

  if (platformStoreGet('UPNEXT_ENABLED') === 'false') {
    store.dispatch({
      type: 'SET_UPNEXT_ENABLED',
      payload: false,
    })
  }

  Platform.getMessageInformation()


  if (COMPILING_FOR_TIZEN) {
    try {
      await loadScript('$WEBAPIS/webapis/webapis.js')
      // window.ga('create', configuration.analytics.samsungTrackingId, 'auto')
      // window.ga('set', 'appId', configuration.analytics.samsungAppId)

      // TODO: https://projects.pbs.org/jira/browse/OTTERKIDS-443
      // TODO: https://projects.pbs.org/jira/browse/OTTERGA-592
      // try {
      //   window.ga('set', 'dimension98', window.webapis.productinfo.getFirmware())
      //   window.ga('set', 'dimension99', window.webapis.productinfo.getRealModel())
      // } catch (e) {
      //   // TODO: set default values?
      // }

      registerTizenNumericalKeys()
      registerTizenMediaKeys()

      // Set up TTS
      const onVoiceGuideChange = () => {
        if (
          window.webapis.tvinfo.getMenuValue(
            window.webapis.tvinfo.TvInfoMenuKey.VOICE_GUIDE_KEY
          ) !== 0
        ) {
          store.dispatch({
            type: 'UPDATE_DEVICE',
            payload: {
              ttsEnabled: true,
            },
          })
        } else {
          store.dispatch({
            type: 'UPDATE_DEVICE',
            payload: {
              ttsEnabled: false,
            },
          })
        }
      }

      window.webapis.tvinfo.addCaptionChangeListener(
        window.webapis.tvinfo.TvInfoMenuKey.VOICE_GUIDE_KEY,
        onVoiceGuideChange
      )
      onVoiceGuideChange()

      try {
        window.webapis.tvinfo.registerInAppCaptionControl(true)
      } catch (error) {
        triggerSentryError(
          'error with registerInAppCaptionControl',
          'index.js',
          {
            errorMessage: error.message ?? 'Undefined error message (default)',
            errorName: error.name ?? 'An error occurred (default)',
          }
        )
      }
      // When launching on a Tizen TV, if the tv setting is on, captions are on.
      // If the tv setting is off, we look at localstorage.
      captionsOn =
        window.webapis.tvinfo.getMenuValue(
          window.webapis.tvinfo.TvInfoMenuKey.CAPTION_ONOFF_KEY
        ) === 1

      // get system caption style key values, preprocess them, and store them
      const mappedStyles = getStylesTizen()
      mappedStyles.enabled = captionsOn
      store.dispatch({
        type: 'SET_CAPTION_STATE',
        payload: mappedStyles,
      })

      window.webapis.tvinfo.showCaption(captionsOn)

      try {
        const deviceModel = window.webapis.productinfo.getModel()
        store.dispatch({
          type: 'UPDATE_DEVICE',
          payload: { deviceModel },
        })
      } catch (e) {
        store.dispatch({
          type: 'UPDATE_DEVICE',
          payload: { deviceModel: 'UNKNOWN TIZEN' },
        })
        triggerSentryError(
          'error with window.webapis.productinfo.getModel()',
          'index.js',
          {
            errorMessage: e.message ?? 'Undefined error message (default)',
            errorName: e.name ?? 'An error occurred (default)',
          }
        )
      }

      try {
        const ppid = await getSponsorshipInfoTizen()

        store.dispatch({
          type: 'UPDATE_DEVICE',
          payload: { ...ppid },
        })
      } catch (e) {
        triggerSentryError('Error configuring Tizen specific', 'index.js', {
          errorMessage: e.message ?? 'Undefined error message (default)',
          errorName: e.name ?? 'An error occurred (default)',
        })
      }
    } catch (error) {
      triggerSentryError('Error configuring Tizen general', 'index.js', {
        errorName: error.name ?? 'An error occurred (default)',
        errorMessage: error.message ?? 'Undefined error message (default)',
      })
    }
  } else if (COMPILING_FOR_VIZIO) {
    await loadScript('http://localhost:12345/scfs/cl/js/vizio-companion-lib.js')

    await (async () =>
      new Promise((res) => {
        document.addEventListener('VIZIO_LIBRARY_DID_LOAD', (e) => {
          res(e)
        })
      }))()

    // window.ga('create', configuration.analytics.vizioTrackingId, 'auto')
    // window.ga('set', 'appId', configuration.analytics.vizioAppId)
    // TODO: https://projects.pbs.org/jira/browse/OTTERKIDS-443
    // TODO: https://projects.pbs.org/jira/browse/OTTERGA-592
    // try {
    //    window.VIZIO.getFirmwareVersion(function (firmwareVersion) {
    //     window.ga('set', 'dimension98', firmwareVersion)
    //   })
    //   window.ga(
    //     'set',
    //     'dimension99',
    //      window.VIZIO.deviceModel ?  window.VIZIO.deviceModel : 'Vizio'
    //   )
    // } catch (error) {
    //   // TODO: set default values?
    // }
    // set up TTS
    const vizioProperties = await window.VIZIO.getDeviceManifest()
    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: {
        ttsEnabled: vizioProperties.tts_enabled,
      },
    })
    document.addEventListener('VIZIO_TTS_DISABLED', () => {
      store.dispatch({
        type: 'UPDATE_DEVICE',
        payload: {
          ttsEnabled: false,
        },
      })
    })

    document.addEventListener('VIZIO_TTS_ENABLED', () => {
      store.dispatch({
        type: 'UPDATE_DEVICE',
        payload: {
          ttsEnabled: true,
        },
      })
    })

    const ppid = await getSponsorshipInfoVIZIO()
    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: { ...ppid },
    })

    window.VIZIO.setClosedCaptionHandler((enabled) => {
      store.dispatch({
        type: 'SET_CAPTION_STATE',
        payload: { enabled },
      })
    })
    window.VIZIO.setClosedCaptionStyleChangeHandler((value) => {
      const mappedStyles = getStylesVIZIO(value)
      try {
        store.dispatch({
          type: 'SET_CAPTION_STATE',
          payload: mappedStyles,
        })
      } catch (error) {
        triggerSentryError('Error configuring Vizio specific', 'index.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorName: error.name ?? 'An error occurred (default)',
        })
      }
    })

    const { deviceModel } = window.VIZIO

    store.dispatch({
      type: 'UPDATE_DEVICE',
      payload: { deviceModel },
    })
  } else if (COMPILING_FOR_COMCASTX1) {
    import('@firebolt-js/sdk').then(async (firebolt) => {
      const { Accessibility } = firebolt
      const { Lifecycle } = firebolt
      const { Discovery } = firebolt
      let timeOutId
      const listenerId = await Lifecycle.listen((event, { previous }) => {
        switch (event) {
          case Lifecycle.LifecycleState.UNLOADING:
            Discovery.clear(listenerId)
            Lifecycle.finished()
            break
          case Lifecycle.LifecycleState.INACTIVE:
            try {
              const fromStates = [
                Lifecycle.LifecycleState.BACKGROUND,
                Lifecycle.LifecycleState.FOREGROUND,
              ]
              if (fromStates.includes(previous)) {
                const { href } = window.location
                if (href.includes('/video-playback/')) {
                  const urlParams =
                    window.location.href.split('/video-playback/')
                  if (urlParams.length === 2) {
                    window.location.href = `${urlParams[0]}/video-details/${urlParams[1]}`
                  } else {
                    window.location.href = '/home'
                  }
                } else if (!href.includes('/home')) {
                  timeOutId = setTimeout(() => {
                    window.location.href = '/home'
                  }, 60 * 1000)
                }
              }
            } catch (error) {
              triggerSentryError(
                'Failed when trying to handle redirections in Comcast Lifecycle',
                'index.js',
                {
                  errorMessage:
                    error.message ?? 'Undefined error message (default)',
                  errorName: error.name ?? 'An error occurred (default)',
                }
              )
            }
            break
          case Lifecycle.LifecycleState.BACKGROUND:
          case Lifecycle.LifecycleState.FOREGROUND:
            if (previous === Lifecycle.LifecycleState.INACTIVE) {
              clearTimeout(timeOutId)
            }
            break
          default:
            break
        }
      })
      // window.ga('create', configuration.analytics.comcastTrackingId, 'auto')
      // window.ga('set', 'appId', configuration.analytics.comcastAppId)

      // TODO: Get and set firmware and and model (dimension98)

      let ttsEnabled = false
      try {
        const vgSettings = await Accessibility.voiceGuidanceSettings()
        ttsEnabled = vgSettings.enabled
      } catch (error) {
        triggerSentryError('Error getting TTS', 'index.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorName: error.name ?? 'An error occurred (default)',
        })
      }
      const ppid = await getSponsorshipInfoComcast()

      const deviceModel = await firebolt.Device.model()
      const distributor = await firebolt.Device.distributor()

      try {
        const ccSettings = await Accessibility.closedCaptionsSettings()
        const isXumo = distributor === 'xglobal'
        captionsOn = ccSettings.enabled ? true : captionsOn
        const mappedStyles = getStylesComcast(ccSettings, isXumo)
        mappedStyles.enabled = captionsOn
        store.dispatch({
          type: 'SET_CAPTION_STATE',
          payload: mappedStyles,
        })
      } catch (error) {
        triggerSentryError('error getting cc settings', 'index.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorName: error.name ?? 'An error occurred (default)',
        })
      }

      store.dispatch({
        type: 'UPDATE_DEVICE',
        payload: {
          deviceModel,
          distributor,
          ttsEnabled,
          ...ppid,
        },
      })
    })
  } else if (COMPILING_FOR_LGTV) {
    // Future LG code here
  } else {
    // window.ga('create', configuration.analytics.samsungTrackingId, 'auto')
    // window.ga('set', 'appId', configuration.analytics.browserAppId)
    // When launching on a browser, enable captions depending on localStorage

    store.dispatch({
      type: 'SET_CAPTION_STATE',
      payload: { enabled: captionsOn },
    })
  }

  tts.pushTextToRead('PBS Video App Launching')
  checkDeviceId()
  googleAnalytics4.initializeGA4()

  const container = document.getElementById('root')
  const root = createRoot(container)

  try {
    await initActivationVerification()
  } catch (error) {
    if (error?.name !== 'ActivationRequiredError') {
      // eslint-disable-next-line react/jsx-filename-extension
      root.render(<AppLaunchErrorScreen error={error} />)
      logClientEvent('error', 'GA client launch failed', {
        name: error?.name,
        message: error?.message,
      })
      return
    }
  }
  root.render(
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </Provider>
  )
  logClientEvent('info', 'GA client launched')

  document.addEventListener('keydown', onKeyDown(store))
}

document.addEventListener('DOMContentLoaded', main)
